import { InsightEvent, LabelMapping, ProcessedInsight } from "./types";

const MIN_DURATION_MS = 1000;

const COLORS = [
  "#FF6B6B",
  "#4ECDC4",
  "#45B7D1",
  "#96CEB4",
  "#FFEEAD",
  "#D4A5A5",
  "#9B59B6",
  "#3498DB",
  "#E67E22",
  "#1ABC9C",
];

export const toStringList = (val: unknown): string[] => {
  const norm = (arr: unknown[]) =>
    arr
      .map((v) => {
        if (typeof v === "string") return v.trim();
        if (v && typeof v === "object") {
          const obj = v as Record<string, unknown>;
          if (typeof obj.S === "string") return obj.S.trim();
          try {
            return JSON.stringify(obj);
          } catch {
            return String(v);
          }
        }
        return String(v).trim();
      })
      .filter((s) => s.length > 0);

  try {
    if (Array.isArray(val)) return norm(val);
    if (typeof val === "string") {
      const t = val.trim();
      if (!t) return [];
      try {
        const parsed = JSON.parse(t);
        if (Array.isArray(parsed)) return norm(parsed);
      } catch {
        // not JSON array
      }
      return t === "[]" ? [] : [t];
    }
    return [];
  } catch {
    return [];
  }
};

export const getDisplayName = (
  originalName: string,
  labelMappings: LabelMapping[]
): string => {
  const mapping = labelMappings.find((m) => m.original === originalName);
  return mapping ? mapping.label : originalName;
};

export const hashColor = (key: string): string => {
  const hashCode = key.split("").reduce((acc, char) => {
    return char.charCodeAt(0) + ((acc << 5) - acc);
  }, 0);
  return COLORS[Math.abs(hashCode) % COLORS.length];
};

export const getEventColor = (event: InsightEvent, id: string): string => {
  const errors = toStringList(event.errors);
  const warnings = toStringList(event.warnings);
  if (errors.length > 0) return "#DC2626";
  if (warnings.length > 0) return "#D97706";
  return hashColor(id);
};

export const processInsights = (
  data: InsightEvent[],
  labelMappings: LabelMapping[],
  activeSuppressed: Set<string>
): {
  insights: ProcessedInsight[];
  timeRange: { min: number; max: number };
  groups: { id: string; label: string }[];
} => {
  const insights: ProcessedInsight[] = [];

  for (const event of data) {
    if (!event?.artifact) continue;
    if (activeSuppressed.has(event.artifact)) continue;

    const startTime =
      typeof event.timestamp === "number"
        ? event.timestamp
        : Number(event.timestamp) || 0;
    const timeTaken =
      typeof event.time_taken_ms === "number"
        ? event.time_taken_ms
        : Number(event.time_taken_ms) || 0;
    const isInstant = timeTaken <= 0;
    const duration = isInstant ? MIN_DURATION_MS : timeTaken;
    const endTime = startTime + duration;
    const id = `${event.artifact}::${startTime}::${insights.length}`;

    insights.push({
      id,
      patient_id: String(event.patient_id ?? ""),
      artifact: event.artifact,
      startTime,
      endTime,
      duration,
      time_taken_ms: timeTaken,
      summary: event.summary ?? null,
      errors: toStringList(event.errors),
      warnings: toStringList(event.warnings),
      color: getEventColor(event, id),
      isInstant,
    });
  }

  if (insights.length === 0) {
    return { insights: [], timeRange: { min: 0, max: 0 }, groups: [] };
  }

  let min = Infinity;
  let max = -Infinity;
  const groupOrder: string[] = [];
  const groupSet = new Set<string>();
  for (const insight of insights) {
    if (insight.startTime < min) min = insight.startTime;
    if (insight.endTime > max) max = insight.endTime;
    if (!groupSet.has(insight.artifact)) {
      groupSet.add(insight.artifact);
      groupOrder.push(insight.artifact);
    }
  }

  const groups = groupOrder.map((artifact) => ({
    id: artifact,
    label: getDisplayName(artifact, labelMappings),
  }));

  return {
    insights,
    timeRange: { min, max },
    groups,
  };
};

export const buildDefaultSuppressed = (
  labelMappings: LabelMapping[]
): Set<string> => {
  const set = new Set<string>();
  for (const m of labelMappings || []) {
    if (m?.suppress === true && typeof m.original === "string") {
      set.add(m.original);
    }
  }
  return set;
};

export type ParsedInsightSummary = {
  formatted: string;
  parsed: unknown;
  isFlatObject: boolean;
};

export const formatSummaryValue = (value: unknown): string => {
  if (value === null || value === undefined) return "—";
  if (typeof value === "string") return value;
  if (typeof value === "number" || typeof value === "boolean") {
    return String(value);
  }
  try {
    return JSON.stringify(value, null, 2);
  } catch {
    return String(value);
  }
};

export const parseInsightSummary = (
  summary: string | null | undefined
): ParsedInsightSummary | null => {
  if (!summary) return null;
  const trimmed = summary.trim();
  if (!trimmed) return null;

  try {
    const parsed = JSON.parse(trimmed);
    const formatted = JSON.stringify(parsed, null, 2);
    const isFlatObject =
      parsed !== null &&
      typeof parsed === "object" &&
      !Array.isArray(parsed) &&
      Object.values(parsed as Record<string, unknown>).every(
        (v) => v === null || typeof v !== "object"
      );

    return { formatted, parsed, isFlatObject };
  } catch {
    return { formatted: trimmed, parsed: null, isFlatObject: false };
  }
};
